import { useState, useCallback, Suspense } from 'react';
import { motion } from 'framer-motion';
import { Search, Plus } from 'lucide-react';
import { graphql, useLazyLoadQuery, useMutation } from 'react-relay';
import { Button } from '@lecartte/shared-ui/components/Button';
import { FormInput } from '@lecartte/shared-ui/components/FormInput';
import { FormTextarea } from '@lecartte/shared-ui/components/FormInput';
import { CloseButton } from '@lecartte/shared-ui/components/RoundIconButton';
import type { FeatureFlagsPageQuery } from '@/__generated__/FeatureFlagsPageQuery.graphql';
import type { FeatureFlagsPageUpsertMutation } from '@/__generated__/FeatureFlagsPageUpsertMutation.graphql';

const QUERY = graphql`
  query FeatureFlagsPageQuery {
    adminFeatureFlags {
      slug
      name
      description
      enabled
      updatedAt
    }
  }
`;

const UPSERT_MUTATION = graphql`
  mutation FeatureFlagsPageUpsertMutation($input: UpsertFeatureFlagInput!) {
    UpsertFeatureFlag(input: $input) {
      flag {
        slug
        description
      }
      error
    }
  }
`;

type FeatureFlag = {
  readonly slug: string;
  readonly name: string;
  readonly description: string;
  readonly enabled: boolean;
  readonly updatedAt: string | null;
};

function formatRelativeTime(dateStr: string | null): string {
  if (!dateStr) return '—';
  const diff = Date.now() - new Date(dateStr).getTime();
  const minutes = Math.floor(diff / 60000);
  if (minutes < 1) return 'agora mesmo';
  if (minutes < 60) return `${minutes} min atrás`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h atrás`;
  const days = Math.floor(hours / 24);
  return `${days}d atrás`;
}

function Toggle({
  checked,
  onChange,
  disabled,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed ${
        checked ? 'bg-primary' : 'bg-slate-300'
      }`}
    >
      <span
        className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${
          checked ? 'translate-x-6' : 'translate-x-1'
        }`}
      />
    </button>
  );
}


function CreateFlagModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: ({ slug }: { slug: string }) => void;
}) {
  const [slug, setSlug] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [commitUpsert] = useMutation<FeatureFlagsPageUpsertMutation>(UPSERT_MUTATION);

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!slug.trim()) return;
    setSaving(true);
    setError(null);
    const normalizedSlug = slug.trim().toUpperCase().replace(/\s+/g, '_');
    commitUpsert({
      variables: { input: { slug: normalizedSlug, name: name.trim() || null, description: description.trim() || null, enabled: true } },
      onCompleted: () => {
        onCreated({ slug: normalizedSlug });
        setSaving(false);
      },
      onError: (err) => {
        setError(err.message);
        setSaving(false);
      },
    });
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        transition={{ duration: 0.2 }}
        className="absolute inset-0 bg-black/40"
        onClick={onClose}
      />
      <motion.div
        initial={{ opacity: 0, y: 24, scale: 0.97 }}
        animate={{ opacity: 1, y: 0, scale: 1 }}
        transition={{ duration: 0.28, ease: [0.16, 1, 0.3, 1] }}
        className="relative z-10 bg-white rounded-2xl shadow-xl w-full max-w-md mx-4 p-6 flex flex-col gap-4"
      >
        <div className="flex items-center justify-between">
          <h2 className="font-display font-bold text-lg text-slate-900">Nova Feature Flag</h2>
          <CloseButton onClick={onClose} />
        </div>

        {error && (
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-3 text-red-600 text-sm">
            {error}
          </div>
        )}

        <form onSubmit={handleSubmit} className="flex flex-col gap-3">
          <FormInput
            label="Slug (chave)"
            type="text"
            value={slug}
            onChange={(e) => setSlug(e.target.value.toUpperCase().replace(/[^A-Z0-9_]/g, ''))}
            placeholder="EX: MINHA_FEATURE"
            required
            className="font-mono"
          />
          <p className="text-xs text-slate-400 -mt-2">Somente letras maiúsculas, números e _</p>

          <FormInput
            label="Nome (opcional)"
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="Ex: Pedido por Mesa"
          />

          <FormTextarea
            label="Descrição (opcional)"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="O que esta feature habilita..."
            rows={2}
          />

          <div className="flex gap-2 pt-1">
            <Button type="button" variant="outline" onClick={onClose} className="flex-1">
              Cancelar
            </Button>
            <Button type="submit" disabled={saving || !slug.trim()} loading={saving} className="flex-1">
              Criar
            </Button>
          </div>
        </form>
      </motion.div>
    </div>
  );
}

function FlagsTable({
  search,
  onShowCreate,
  fetchKey,
}: {
  search: string;
  onShowCreate: () => void;
  fetchKey: number;
}) {
  const [toggling, setToggling] = useState<string | null>(null);
  const [localOverrides, setLocalOverrides] = useState<Record<string, boolean | undefined>>({});
  const data = useLazyLoadQuery<FeatureFlagsPageQuery>(QUERY, {}, { fetchKey, fetchPolicy: 'network-only' });
  const [commitUpsert] = useMutation<FeatureFlagsPageUpsertMutation>(UPSERT_MUTATION);

  const handleToggle = useCallback(
    ({ slug, value }: { slug: string; value: boolean }) => {
      setLocalOverrides((prev) => ({ ...prev, [slug]: value }));
      setToggling(slug);
      commitUpsert({
        variables: { input: { slug, enabled: value } },
        onCompleted: () => setToggling(null),
        onError: () => {
          setLocalOverrides((prev) => {
            const next = { ...prev };
            delete next[slug];
            return next;
          });
          setToggling(null);
        },
      });
    },
    [commitUpsert]
  );

  const flags = data.adminFeatureFlags as readonly FeatureFlag[];
  const filtered = flags.filter(
    (f) =>
      search === '' ||
      f.slug.toLowerCase().includes(search.toLowerCase()) ||
      f.name.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
      <table className="w-full text-sm">
        <thead className="bg-slate-50 border-b border-slate-200">
          <tr>
            <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide">
              Feature Key
            </th>
            <th className="text-center px-4 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide w-28">
              Ativo
            </th>
            <th className="text-left px-5 py-3 text-xs font-semibold text-slate-500 uppercase tracking-wide w-36">
              Last updated
            </th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {filtered.length === 0 ? (
            <tr>
              <td colSpan={3} className="px-5 py-10 text-center text-slate-400 text-sm">
                Nenhuma feature encontrada.
              </td>
            </tr>
          ) : (
            filtered.map((flag) => {
              const isToggling = toggling === flag.slug;
              const effective = localOverrides[flag.slug] !== undefined ? localOverrides[flag.slug]! : flag.enabled;
              return (
                <tr key={flag.slug} className="hover:bg-slate-50/60 transition-colors">
                  <td className="px-5 py-4">
                    <span className="font-mono text-sm font-semibold text-slate-800 tracking-tight">
                      {flag.slug}
                    </span>
                    <p className="text-xs text-slate-400 mt-0.5 font-sans">{flag.name}</p>
                  </td>
                  <td className="px-4 py-4 text-center">
                    <div className="flex justify-center">
                      <Toggle
                        checked={effective}
                        disabled={isToggling}
                        onChange={(v) => handleToggle({ slug: flag.slug, value: v })}
                      />
                    </div>
                  </td>
                  <td className="px-5 py-4 text-xs text-slate-400">
                    {formatRelativeTime(flag.updatedAt)}
                  </td>
                </tr>
              );
            })
          )}
        </tbody>
      </table>
    </div>
  );
}

export function FeatureFlagsPage() {
  const [search, setSearch] = useState('');
  const [showCreate, setShowCreate] = useState(false);
  const [fetchKey, setFetchKey] = useState(0);

  return (
    <div className="flex flex-col gap-5">
      {showCreate && (
        <CreateFlagModal
          onClose={() => setShowCreate(false)}
          onCreated={() => { setShowCreate(false); setFetchKey((k) => k + 1); }}
        />
      )}

      <div className="flex items-start justify-between gap-4">
        <div>
          <h1 className="font-display font-bold text-2xl text-slate-900">Features</h1>
          <p className="text-slate-500 text-sm mt-1">
            Controle global de funcionalidades.
          </p>
        </div>
        <Button onClick={() => setShowCreate(true)}>
          <Plus size={15} />
          Nova flag
        </Button>
      </div>

      <div className="relative max-w-xs">
        <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 z-10" />
        <FormInput
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Filtrar lista..."
          className="pl-8"
        />
      </div>

      <Suspense
        fallback={
          <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-10 text-center text-slate-400 text-sm">
            Carregando...
          </div>
        }
      >
        <FlagsTable search={search} onShowCreate={() => setShowCreate(true)} fetchKey={fetchKey} />
      </Suspense>
    </div>
  );
}
